import { createClient } from '../../prismicio'
import { Constants, Utils } from 'common/utils'
import { getPosts, getTags, transformBlogPost } from 'common/cms-sdk'
import {
  TBlogPost,
  TBlogPostResult,
  TBlogPostSummary,
  TPlanSummary,
} from 'common/cms-types'
import { I18NConfig } from 'next/dist/server/config-shared'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import SeoContainer from 'components/util/containers/SeoContainer'
import { slimDocument } from 'common/cms-helpers'
import { RichText, TableOfContents } from 'components/forms/RichText'
import { ArticleJsonLd, NextSeoProps } from 'next-seo'
import RelatedBlogPost from 'components/blog/RelatedBlogPost'
import PrismicContainer from 'components/util/containers/PrismicContainer'
import { seoStrings } from 'common/strings/seo-strings'
import FAQList from 'components/util/FAQList'
import { ItemListElements } from 'next-seo/lib/types'
import { blogBreadcrumb } from 'components/util/containers/Breadcrumbs'
import Strings from 'project/localisation'
import PrimeLondonBusinessAddressCard from 'components/cards/PrimeLondonBusinessAddressCard'
import AddressSection from 'components/sections/AddressSection'
import { blogStrings } from 'common/strings/blog-strings'
import Button from 'components/forms/Button'
import useWindowSize from 'components/util/useWindowSize'
import classNames from 'classnames'
import BlogSearch from 'components/blog/BlogSearch'
import { Query, RichTextField } from '@prismicio/types'
import useThrottle from 'common/useThrottle'
import standardiseTitle from 'project/standardiseTitle'
import usePreview from 'project/usePreview'
import BlogPostProductCTA from 'components/BlogPostProductCTA'
import { renderToString } from 'preact-render-to-string'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import LondonVOPlusHeroCard from 'components/cards/new/LondonVOPlusHeroCard'
import { PlanSlug } from 'common/planSlugs'
import { getPlans } from 'common/planData'
import { TableSlice } from 'components/util/Table'

const PAGE_SIZE = 16
const RELATED_ARTICLE_PAGE_SIZE = 6
export type BlogPostType = {
  // @ts-ignore
  tags?: string[]
  tag?: string
  post: TBlogPost | null
  relatedArticles: TBlogPostSummary[]
  plans?: TPlanSummary[]
}
const BlogPostPage: NextPageWithLayout<BlogPostType> = ({
  plans,
  post,
  relatedArticles,
  tag,
  tags,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [posts, setPosts] = useState<Query<TBlogPostResult>>()
  const [pageNumber, setPageNumber] = useState(1)
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const { preview } = usePreview()
  useEffect(() => {
    if (!post) {
      router.replace('/blog')
    }

    fetch(false)
  }, [router, post, query])

  const searchItems = useThrottle(() => {
    if (query !== search) {
      setQuery(search)
    }
  }, 200)

  useEffect(() => {
    searchItems(search)
  }, [search])

  const fetch = (next: boolean) => {
    setIsLoading(true)
    getPosts(
      createClient(Utils.getClientContext(router)),
      next ? pageNumber + 1 : pageNumber,
      Utils.getLocaleID(`${router.locale}`),
      PAGE_SIZE,
      next ? posts : undefined,
      tag,
      query,
    )
      .then((res) => {
        if (next) {
          setPageNumber(pageNumber + 1)
        } else {
          setPageNumber(1)
        }
        setPosts(res)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  if (!post) {
    return null
  }
  const { data } = post

  const title = standardiseTitle(`${data.meta_data_title} | Hoxton Mix`)
  const seoProps: NextSeoProps = {
    description: data.body_excerpt,
    openGraph: {
      article: {
        authors: ['https://www.facebook.com/hoxtonmix/'],
        modifiedTime: data.modified_date_iso,
        publishedTime: data.published_date_iso,
      },
      images: [Utils.prismicImageToOpenGraph(data.main_image)].filter((v)=>!!v),
      title,
      type: 'article',
      url: `https://www.hoxtonmix.com/blog/${data.uid}/`,
    },
    title,
    twitter: {
      cardType: 'summary_large_image',
    },
  }
  const breadcrumb: ItemListElements[] = blogBreadcrumb().concat([
    {
      item: `https://www.hoxtonmix.com/blog/${data.uid}/`,
      name: `${Strings.meetingRooms}`,
      position: 3,
    },
  ])
  const windowSize = useWindowSize()

  // Combine body content with all content_area slices for Table of Contents
  const allContent = [
    ...data.body,
    ...data.slices
      .filter((slice) => slice.slice_type === 'content_area')
      .flatMap((slice) =>
        slice.slice_type === 'content_area' ? slice.primary.body : [],
      ),
  ] as RichTextField

  const contents = (
    <TableOfContents isBlog text={allContent} shareTitle={data.title} />
  )
  const stringContents = renderToString(contents)
  const hideContents = stringContents?.length === 0

  return (
    <PrismicContainer>
      <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
        <main
          className={classNames({
            'position-relative': !windowSize.isXlOrHigher,
          })}
        >
          <BlogSearch
            key={post.id}
            posts={search ? posts : null}
            tags={tags}
            tag={tag}
            search={search}
            setSearch={setSearch}
            onShowMoreClick={() => fetch(false)}
            isLoading={isLoading}
          />
          {!search && (
            <>
              <div className='container px-xxl-52 px-xl-28 px-5 mt-14 mb-md-34 mb-14'>
                <div className={'px-md-20'}>
                  {!!data.published_date && (
                    <p
                      className={
                        'text-small-m text-small-m--mobile-small text-muted mb-md-10 mb-6'
                      }
                    >
                      {data.published_date}
                    </p>
                  )}
                  <h1
                    className={
                      'h3 h3--large text-bold mb-xxxl-18 mb-md-10 mb-2'
                    }
                  >
                    {data.title}{' '}
                    {!!preview && (
                      <div className='py-2'>
                        <Button
                          onClick={() =>
                            window.open(
                              `https://hoxton-mix.prismic.io/builder/pages/${post.id}`,
                            )
                          }
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </h1>
                </div>
                <div className={'rich-text-blog'}>
                  {!hideContents && (
                    <>
                      <h4>Table of Contents</h4>
                      <div className='p-2 mb-18 rounded bg-grey4'>
                        <div
                          style={{
                            display: 'grid',
                            gridAutoFlow: 'row',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                          }}
                        >
                          {contents}
                        </div>
                      </div>
                    </>
                  )}
                  <RichText isBlog text={data.body} shareTitle={data.title} />
                  {data.slices.map((slice, index) => {
                    if (slice.slice_type === 'table') {
                      return (
                        <TableSlice
                          key={index}
                          slice={slice}
                          className='text-center'
                        />
                      )
                    }
                    if (slice.slice_type === 'content_area') {
                      return (
                        <RichText
                          key={index}
                          isBlog
                          text={slice.primary.body}
                        />
                      )
                    }
                    return null
                  })}
                  <div className='mt-10'>
                    <BlogPostProductCTA />
                  </div>
                  <FAQList
                    styledAsRichTextForBlog={true}
                    slices={
                      data.slices.filter(
                        (slice) => slice.slice_type === 'faq',
                      ) as any
                    }
                  />
                </div>
              </div>
              {!!relatedArticles.length && (
                <div className={'blog-results bg-grey6 pt-lg-18 pt-12'}>
                  <div className={'container px-xxl-27 px-lg-28 px-3-half'}>
                    <h2
                      className={classNames(
                        'text-center text-semibold mb-lg-18 mb-12',
                        windowSize.isSmOrLower ? 'text-xl' : 'h2',
                      )}
                    >
                      {blogStrings.handpickedRelatedArticles}
                    </h2>
                    <div className='row'>
                      {relatedArticles.map((post) => (
                        <div className={'col-xl-4 col-md-6 mb-6'}>
                          <RelatedBlogPost key={post.id} post={post} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div className='container mt-xl-38 mt-68 pb-10'>
            <div className='container new-design mt-xl-38 mt-68 pb-10'>
              <div className='row gy-5 mt-10 mt-lg-22'>
                <div className='col-12 col-lg-6 '>
                  <ContactUsCard />
                </div>
                <div className='col-12 col-lg-6'>
                  <LondonVOPlusHeroCard
                    planSummary={
                      plans?.find(
                        (v: TPlanSummary) => v.slug === PlanSlug.SCAN_LITE,
                      )!
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <ArticleJsonLd
            type='Article'
            url={`https://www.hoxtonmix.com/blog/${data.uid}`}
            title={data.title}
            images={[data.main_image?.url]}
            publisherName={Constants.companyInformation.name}
            publisherLogo={seoStrings.appOGLogo}
            datePublished={data.published_date_iso}
            dateModified={data.modified_date_iso}
            authorName={'Chris Sees'}
            description={data.body_excerpt}
          />
        </main>
      </SeoContainer>
    </PrismicContainer>
  )
}

export async function getStaticPaths(c: I18NConfig) {
  const documents = await createClient().getAllByType('blog_post', {
    limit: process.env.BLOG_LIMIT
      ? parseInt(process.env.BLOG_LIMIT)
      : undefined,
  })
  return {
    fallback: 'blocking',
    paths: documents
      .map((doc) => {
        return c.locales.map((locale: string) => ({
          locale,
          params: { uid: doc.uid },
        }))
      })
      .flat(),
  }
}
export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const tags = await getTags(client, lang)
    let post: TBlogPost | null = null
    let relatedArticles: TBlogPostSummary[] = []
    try {
      const fullPost = (await client.getByUID('blog_post', context.params.uid, {
        lang,
      })) as unknown as TBlogPost
      post = await slimDocument(await transformBlogPost(fullPost))
      const posts = await getPosts(
        client,
        1,
        lang,
        RELATED_ARTICLE_PAGE_SIZE + 1,
        undefined,
        fullPost?.tags?.[0],
      )
      relatedArticles = posts.results
        .filter((v) => {
          return v.id !== post?.id
        })
        .filter((_, i) => i < RELATED_ARTICLE_PAGE_SIZE)
        .map((article) => {
          return slimDocument({
            ...article,
            data: {
              main_image: article.data.main_image,
              published_date: article.data.modified_date,
              title: article.data.title,
            },
          })
        })
      // @ts-ignore
      post.data.uid = context.params.uid
    } catch {
      // If we reach this line, it means the post doesn't exist for this locale
    }

    return {
      props: {
        plans,
        post,
        posts,
        relatedArticles,
        tags,
      },
    }
  } catch (e) {
    return {
      props: {
        post: null,
      },
    }
  }
}

export default BlogPostPage
